import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import Layout from './components/Layout.jsx'
import ErrorBoundary from './components/ErrorBoundary.jsx'
import FeatureBlockedPage from './components/FeatureBlockedPage.jsx'
import AdminAccessPage from './pages/AdminAccessPage.jsx'
import LoginPage from './pages/LoginPage.jsx'
import Dashboard from './pages/Dashboard.jsx'
import StudentsPage from './pages/StudentsPage.jsx'
import TeachersPage from './pages/TeachersPage.jsx'
import TeacherProfilePage from './pages/TeacherProfilePage.jsx'
import UsersPage from './pages/UsersPage.jsx'
import UserProfilePage from './pages/UserProfilePage.jsx'
import ClassesPage from './pages/ClassesPage.jsx'
import SubjectsPage from './pages/SubjectsPage.jsx'
import AttendancePage from './pages/AttendancePage.jsx'
import TimetablePage from './pages/TimetablePage.jsx'
import LeavesPage from './pages/LeavesPage.jsx'
import ExamSessionsPage from './pages/ExamSessionsPage.jsx'
import ExamsPage from './pages/ExamsPage.jsx'
import ExamResultSheetPage from './pages/ExamResultSheetPage.jsx'
import ResultsPage from './pages/ResultsPage.jsx'
import ReportCardsPage from './pages/ReportCardsPage.jsx'
import ReportCardDetailPage from './pages/ReportCardDetailPage.jsx'
import FeeStructuresPage from './pages/FeeStructuresPage.jsx'
import FeeAccountsPage from './pages/FeeAccountsPage.jsx'
import FeeAccountDetailPage from './pages/FeeAccountDetailPage.jsx'
import FeeCollectionPage from './pages/FeeCollectionPage.jsx'
import FeeDefaultersPage from './pages/FeeDefaultersPage.jsx'
import FeeManagementPage from './pages/FeeManagementPage.jsx'
import FeeAnalyticsPage from './pages/FeeAnalyticsPage.jsx'
import FeeReceiptPage from './pages/FeeReceiptPage.jsx'
import NotificationsPage from './pages/NotificationsPage.jsx'
import CalendarPage from './pages/CalendarPage.jsx'
import GalleryPage from './pages/GalleryPage.jsx'
import MarksheetPage from './pages/MarksheetPage.jsx'
import MessagesPage from './pages/MessagesPage.jsx'
import ManageRelationsPage from './pages/ManageRelationsPage.jsx'
import BulkUploadPage from './pages/BulkUploadPage.jsx'
import SettingsPage from './pages/SettingsPage.jsx'
import SchoolLandingPage from './pages/SchoolLandingPage.jsx'
import SubscriptionPage from './pages/SubscriptionPage.jsx'
import FeeDiscountsPage from './pages/FeeDiscountsPage.jsx'
import AlumniPage from './pages/AlumniPage.jsx'
import InactiveUsersPage from './pages/InactiveUsersPage.jsx'
import ClassDetailPage from './pages/ClassDetailPage.jsx'
import { schoolsApi } from './api/services.js'
import { isAdminPortalRole } from './constants/roles.js'
import { useAuth } from './hooks/useAuth.js'
import { useFeatureEnabled } from './hooks/useSchoolFeatures.js'
import { setSchoolConfig } from './store/authSlice.js'
import { useDispatch } from 'react-redux'
import { buildSchoolConfigFromSchool } from './utils/schoolConfig.js'
import { applySchoolTheme } from './utils/theme.js'

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return <div className="min-h-screen bg-gray-50" />
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />
}

function AdminRoute({ children }) {
  const { user } = useAuth()
  return isAdminPortalRole(user?.role) ? children : <AdminAccessPage />
}

function FeatureRoute({ feature, children }) {
  const enabled = useFeatureEnabled(feature)
  return enabled ? children : (
    <FeatureBlockedPage
      title="Fees Disabled"
      message="Fee collection, structures, accounts, defaulters, receipts, and adjustments are disabled for this school."
    />
  )
}

export default function App() {
  const dispatch = useDispatch()
  const { isAuthenticated, user, schoolConfig } = useAuth()
  const schoolId = user?.schoolId?._id || user?.schoolId || schoolConfig?._id || null

  useEffect(() => {
    applySchoolTheme(schoolConfig?.theme || null)
  }, [schoolConfig?.theme])

  useEffect(() => {
    if (!isAuthenticated || !schoolId || user?.role === 'super_admin') return

    let cancelled = false

    const refreshSchoolTheme = async () => {
      try {
        const school = await schoolsApi.getById(schoolId)
        if (cancelled) return

        const nextSchoolConfig = buildSchoolConfigFromSchool(school, schoolConfig)
        if (nextSchoolConfig) {
          dispatch(setSchoolConfig(nextSchoolConfig))
        }
      } catch {
        // Keep the cached session config if the refresh fails.
      }
    }

    refreshSchoolTheme()

    const handleFocusRefresh = () => {
      refreshSchoolTheme()
    }

    const handleVisibilityRefresh = () => {
      if (document.visibilityState === 'visible') {
        refreshSchoolTheme()
      }
    }

    window.addEventListener('focus', handleFocusRefresh)
    document.addEventListener('visibilitychange', handleVisibilityRefresh)

    return () => {
      cancelled = true
      window.removeEventListener('focus', handleFocusRefresh)
      document.removeEventListener('visibilitychange', handleVisibilityRefresh)
    }
  }, [dispatch, isAuthenticated, schoolId, user?.role])

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <Layout />
            </AdminRoute>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />

        {/* People */}
        <Route path="users" element={<ErrorBoundary><UsersPage /></ErrorBoundary>} />
        <Route path="users/:userId" element={<ErrorBoundary><UserProfilePage /></ErrorBoundary>} />
        <Route path="profile" element={<ErrorBoundary><UserProfilePage /></ErrorBoundary>} />
        <Route path="students" element={<ErrorBoundary><StudentsPage /></ErrorBoundary>} />
        <Route path="teachers" element={<ErrorBoundary><TeachersPage /></ErrorBoundary>} />
        <Route path="teachers/:teacherId" element={<ErrorBoundary><TeacherProfilePage /></ErrorBoundary>} />
        <Route path="relations" element={<ErrorBoundary><ManageRelationsPage /></ErrorBoundary>} />
        <Route path="parent-student-links" element={<ErrorBoundary><ManageRelationsPage /></ErrorBoundary>} />
        <Route path="bulk-upload" element={<ErrorBoundary><BulkUploadPage /></ErrorBoundary>} />
        <Route path="alumni" element={<ErrorBoundary><AlumniPage /></ErrorBoundary>} />
        <Route path="inactive-users" element={<ErrorBoundary><InactiveUsersPage /></ErrorBoundary>} />

        {/* Academics */}
        <Route path="classes" element={<ErrorBoundary><ClassesPage /></ErrorBoundary>} />
        <Route path="classes/:classId" element={<ErrorBoundary><ClassDetailPage /></ErrorBoundary>} />
        <Route path="subjects" element={<ErrorBoundary><SubjectsPage /></ErrorBoundary>} />
        <Route path="attendance" element={<ErrorBoundary><AttendancePage /></ErrorBoundary>} />
        <Route path="timetable" element={<ErrorBoundary><TimetablePage /></ErrorBoundary>} />
        <Route path="manage-timetable" element={<ErrorBoundary><TimetablePage /></ErrorBoundary>} />
        <Route path="leaves" element={<ErrorBoundary><LeavesPage /></ErrorBoundary>} />
        <Route path="leave-requests" element={<ErrorBoundary><LeavesPage /></ErrorBoundary>} />

        {/* Exams & Results */}
        <Route path="exam-sessions" element={<ErrorBoundary><ExamSessionsPage /></ErrorBoundary>} />
        <Route path="exams" element={<ErrorBoundary><ExamsPage /></ErrorBoundary>} />
        <Route path="exams/:examId/result-sheet" element={<ErrorBoundary><ExamResultSheetPage /></ErrorBoundary>} />
        <Route path="results" element={<ErrorBoundary><ResultsPage /></ErrorBoundary>} />
        <Route path="report-cards" element={<ErrorBoundary><ReportCardsPage /></ErrorBoundary>} />
        <Route path="report-cards/:studentId/:sessionId" element={<ErrorBoundary><ReportCardDetailPage /></ErrorBoundary>} />
        <Route path="marksheets/:studentId/:examId" element={<ErrorBoundary><MarksheetPage /></ErrorBoundary>} />

        {/* Fees */}
        <Route path="fee-management" element={<ErrorBoundary><FeatureRoute feature="fees"><FeeManagementPage /></FeatureRoute></ErrorBoundary>} />
        <Route path="fees/analytics" element={<ErrorBoundary><FeatureRoute feature="fees"><FeeAnalyticsPage /></FeatureRoute></ErrorBoundary>} />
        <Route path="fee-structures" element={<ErrorBoundary><FeatureRoute feature="fees"><FeeStructuresPage /></FeatureRoute></ErrorBoundary>} />
        <Route path="fees/structures" element={<ErrorBoundary><FeatureRoute feature="fees"><FeeStructuresPage /></FeatureRoute></ErrorBoundary>} />
        <Route path="fee-accounts" element={<ErrorBoundary><FeatureRoute feature="fees"><FeeAccountsPage /></FeatureRoute></ErrorBoundary>} />
        <Route path="fees/accounts" element={<ErrorBoundary><FeatureRoute feature="fees"><FeeAccountsPage /></FeatureRoute></ErrorBoundary>} />
        <Route path="fee-accounts/:accountId" element={<ErrorBoundary><FeatureRoute feature="fees"><FeeAccountDetailPage /></FeatureRoute></ErrorBoundary>} />
        <Route path="fees/accounts/:accountId" element={<ErrorBoundary><FeatureRoute feature="fees"><FeeAccountDetailPage /></FeatureRoute></ErrorBoundary>} />
        <Route path="fee-collection" element={<ErrorBoundary><FeatureRoute feature="fees"><FeeCollectionPage /></FeatureRoute></ErrorBoundary>} />
        <Route path="fees/collection" element={<ErrorBoundary><FeatureRoute feature="fees"><FeeCollectionPage /></FeatureRoute></ErrorBoundary>} />
        <Route path="fee-defaulters" element={<ErrorBoundary><FeatureRoute feature="fees"><FeeDefaultersPage /></FeatureRoute></ErrorBoundary>} />
        <Route path="fees/defaulters" element={<ErrorBoundary><FeatureRoute feature="fees"><FeeDefaultersPage /></FeatureRoute></ErrorBoundary>} />
        <Route path="fee-discounts" element={<ErrorBoundary><FeatureRoute feature="fees"><FeeDiscountsPage /></FeatureRoute></ErrorBoundary>} />
        <Route path="fees/discounts" element={<ErrorBoundary><FeatureRoute feature="fees"><FeeDiscountsPage /></FeatureRoute></ErrorBoundary>} />
        <Route path="fees/receipts/:paymentId" element={<ErrorBoundary><FeatureRoute feature="fees"><FeeReceiptPage /></FeatureRoute></ErrorBoundary>} />

        {/* Communication */}
        <Route path="notifications" element={<ErrorBoundary><NotificationsPage /></ErrorBoundary>} />
        <Route path="messages" element={<ErrorBoundary><MessagesPage /></ErrorBoundary>} />
        <Route path="calendar" element={<ErrorBoundary><CalendarPage /></ErrorBoundary>} />
        <Route path="gallery" element={<ErrorBoundary><GalleryPage /></ErrorBoundary>} />

        {/* Admin */}
        <Route path="settings" element={<ErrorBoundary><SettingsPage /></ErrorBoundary>} />
        <Route path="school-settings" element={<ErrorBoundary><SettingsPage /></ErrorBoundary>} />
        <Route path="landing" element={<ErrorBoundary><SchoolLandingPage /></ErrorBoundary>} />
        <Route path="landing-page" element={<ErrorBoundary><SchoolLandingPage /></ErrorBoundary>} />
        <Route path="subscription" element={<ErrorBoundary><SubscriptionPage /></ErrorBoundary>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}
